import { useLife } from '@/state/life';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { addImpulse } from './prices';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Trump Shoutout mini-arc.
 *
 * Triggered when notoriety crosses 65 + maga rep > 20. President posts
 * about you on TruthSocial-clone. 2 reaction decisions decide whether
 * you ride the wave (+30 maga, -20 left, big BOOM impulse) or play it
 * cool (modest tribal swing, less impulse, more press credibility).
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => void }[];
}

const BEATS: Beat[] = [
  {
    text: 'YOUR PR FLACK: "the President just truthed about you. it\'s your photo + 4 American flags + the words \"WINNING.\" how do you respond?"',
    options: [
      { label: 'repost it. add 6 flag emojis and "thank you sir."', effect: () => {
        usePower.getState().bumpRep('maga', 30);
        usePower.getState().bumpRep('left', -20);
        usePower.getState().bumpNotoriety(15);
        addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 30 * 60_000, logDelta: 0.18 });
        useXClone.getState().composePost('thank you, mr president. WAGMI.');
      } },
      { label: 'screenshot it. quietly send to your CFO. say nothing publicly.', effect: () => {
        usePower.getState().bumpRep('press', 6);
        usePower.getState().bumpRep('maga', 5);
        addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 15 * 60_000, logDelta: 0.06 });
      } },
      { label: 'subtweet it. say "i don\'t comment on individual posts."', effect: () => {
        usePower.getState().bumpRep('left', 10);
        usePower.getState().bumpRep('maga', -15);
        usePower.getState().bumpRep('press', 8);
      } },
    ],
  },
  {
    text: 'CNBC wants you for a 4-minute hit on this. agree? decline?',
    options: [
      { label: 'agree. wear a flag pin. lean in.', effect: () => {
        usePower.getState().bumpRep('maga', 12);
        usePower.getState().bumpRep('cnbc', 6);
        usePower.getState().bumpRep('press', -4);
        useLife.getState().bumpSanity(-3);
      } },
      { label: 'decline. tell them you\'re "spending time with family."', effect: () => {
        usePower.getState().bumpRep('press', 4);
        useLife.getState().bumpRelationship('wife', 6);
      } },
      { label: 'agree but wear a "FREE LULA" t-shirt for plausible chaos', effect: () => {
        usePower.getState().bumpNotoriety(20);
        usePower.getState().bumpRep('press', 12);
        usePower.getState().bumpRep('maga', -10);
        useLife.getState().bumpSanity(-2);
      } },
    ],
  },
];

export function startTrumpShoutoutArc(): void {
  useMeta.getState().recordLore('arc-trump-shoutout', 'Presidential Shoutout');
  useCinematic.getState().fire({
    id: `shoutout-${Date.now()}`,
    type: 'breaking',
    headline: 'THE PRESIDENT TRUTHED ABOUT YOU.',
    subhead: 'verified account. 47 million followers. 6 minutes ago. comments are 50/50 cheering/threats. either is engagement.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `shoutout-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `shoutout-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: usePhone.getState().contacts['pr-flack'] ? 'pr-flack' : 'broker',
        fromNpc: usePhone.getState().contacts['pr-flack'] ? 'pr-flack' : 'broker',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `shoutout: react ${i + 1}/2` });
      sounds.notifyAlert();
    }, 5_000 + i * 30_000);
  }

  setTimeout(() => {
    useXClone.getState().receivePost({
      authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
      text: 'BREAKING: a "well-known retail trader" just received a presidential shoutout. analysts split on whether this is good for them, the President, or anyone. likely: nobody.',
      likes: 28000, reposts: 8400,
    });
  }, 5_000 + BEATS.length * 30_000 + 5_000);
}
